import * as Sentry from "@sentry/react";
import { dismissImportConversationStarters } from "./dismissImportConversationStarters";
import { useQueryClient } from "@tanstack/react-query";
import { Editor } from "@tiptap/core";
import { useCallback, useEffect, useRef, useState } from "react";
import Auth from "src/Auth";
import type Project from "src/models/Project";
import { AppEventPublisher } from "src/AppEventPublisher";
import { useCrossAppSignalStore, CrossAppSignal } from "src/stores/crossAppSignalStore";
import { useProjectStore } from "src/stores/projectStore";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { Text } from "src/sudokit/components/Text";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { ChatListView } from "./ChatListView";
import { ChatNavigation } from "./ChatNavigation";
import { PersistentChatProvider } from "./PersistentChatContext";
import { ChatErrorBoundary } from "./PersistentChatErrorBoundary";
import { fetchChatPage, mergeRefetchedChatTail, restoreProjectChatsListCaches, snapshotProjectChatsListCaches, updateProjectChatsListCaches, useCurrentChat, useDeleteChat, useProjectChatsList, useUpdateChat } from "./PersistentChatQueries";
import { chatQueryKey, DefaultChatModel, NEW_CHAT_ID } from "./PersistentChatTypes";
import { PersistentChatUI } from "./PersistentChatUI";
import { usePersistentChatStore } from "src/stores/persistentChatStore";
import { useOnSudoLayoutEvent } from "src/lib/layout-events";
import { PersistentChatWithMessages } from "~shared/types/persistent-chat-types";
import { useAgenticTabAttentionFavicon } from "./useAgenticTabAttentionFavicon";
import { sanitizeChatTitleForDisplay } from "./chatTitleUtils";
import { SharedChatThemeProvider } from "src/components/SharedChatThemeProvider";

type Props = {
  currentChatId: string;
  setCurrentChatId: (chatId: string) => void;
  editor: Editor;
};

const getAgenticChatKey = (pid: string) => `@sudowrite:last-agentic-chat-accessed:${pid}`;
const getLegacyPersistentChatKey = (pid: string) => `@sudowrite:last-chat-accessed:${pid}`;

interface AgenticChatState {
  chatId: string;
  timestamp: number;
  /** Firebase project that owns the chat (may differ from the book being viewed in a series folder). */
  chatProjectId?: string;
}

/** Help menu motion exit is 0.15s (see HelpMenu); small buffer so the line appears after the panel is gone. */
const HELP_MENU_GREETING_AFTER_CLOSE_MS = 200;
const HELP_MENU_GREETING_TEXT = "How can I help you?";
const HELP_MENU_GREETING_METADATA = { source: "help_menu_agentic" as const };

function getAgenticChatState(projectId: string): AgenticChatState | null {
  const raw = localStorage.getItem(getAgenticChatKey(projectId));
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (parsed?.chatId && typeof parsed.chatId === "string") {
      return { chatId: parsed.chatId, timestamp: parsed.timestamp || Date.now() };
    }
    return null;
  } catch {
    return null;
  }
}

function getMigratedLegacyChatState(projectId: string): AgenticChatState | null {
  const raw = localStorage.getItem(getLegacyPersistentChatKey(projectId));
  if (!raw) return null;

  try {
    localStorage.removeItem(getLegacyPersistentChatKey(projectId));
    if (raw.startsWith("{") || raw === NEW_CHAT_ID) return null;
    const state: AgenticChatState = { chatId: raw, timestamp: Date.now() };
    localStorage.setItem(getAgenticChatKey(projectId), JSON.stringify(state));
    return state;
  } catch {
    return null;
  }
}

function getStoredChatState(projectId: string): AgenticChatState | null {
  return getAgenticChatState(projectId) ?? getMigratedLegacyChatState(projectId);
}

/**
 * Persists the "last accessed chat" for a project so PersistentChat restores it
 * on mount regardless of elapsed time. Exported for flows that create a chat server-side
 * and then navigate into the project (e.g. chat onboarding).
 */
export function setAgenticChatState(viewingProjectId: string, chatId: string, chatProjectId?: string): void {
  if (!chatId || chatId === NEW_CHAT_ID) return;
  const state: AgenticChatState = {
    chatId,
    timestamp: Date.now(),
    ...(chatProjectId && chatProjectId !== viewingProjectId ? { chatProjectId } : {}),
  };
  try {
    localStorage.setItem(getAgenticChatKey(viewingProjectId), JSON.stringify(state));
  } catch {}
}

function clearAgenticChatState(projectId: string): void {
  try {
    localStorage.removeItem(getAgenticChatKey(projectId));
  } catch {}
}

const AgenticTabAttentionBridge: React.FC<{ projectId: string; chatId: string }> = ({ projectId, chatId }) => {
  useAgenticTabAttentionFavicon({ projectId, currentChatId: chatId });
  return null;
};

const PersistentChatComponent: React.FC<Props & { project: Project }> = ({ currentChatId, setCurrentChatId: onChatChange, editor, project }) => {
  const selectionVersionRef = useRef(0);
  const [restoringProjectId, setRestoringProjectId] = useState<string | null>(null);
  const setCurrentChatId = useCallback((chatId: string) => {
    selectionVersionRef.current += 1;
    setRestoringProjectId(null);
    onChatChange(chatId);
  }, [onChatChange]);
  const [view, setView] = useState<"chat" | "list">("chat");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [activeChatProjectId, setActiveChatProjectId] = useState(project.id);
  const previousProjectIdRef = useRef<string | null>(null);
  const helpMenuGreetingRevealTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const helpMenuGreetingScheduleGenerationRef = useRef(0);
  const currentChatIdRef = useRef(currentChatId);
  const currentChatIdFromPropsRef = useRef(currentChatId ?? NEW_CHAT_ID);
  const projectIdRef = useRef(project.id);
  const activeChatProjectIdRef = useRef(project.id);
  const queryClient = useQueryClient();

  currentChatIdRef.current = currentChatId ?? NEW_CHAT_ID;
  currentChatIdFromPropsRef.current = currentChatId ?? NEW_CHAT_ID;
  projectIdRef.current = project.id;
  activeChatProjectIdRef.current = activeChatProjectId;

  const dismissHelpMenuGreeting = useCallback(() => {
    helpMenuGreetingScheduleGenerationRef.current += 1;
    if (helpMenuGreetingRevealTimeoutRef.current !== null) {
      clearTimeout(helpMenuGreetingRevealTimeoutRef.current);
      helpMenuGreetingRevealTimeoutRef.current = null;
    }
  }, []);

  const tryDeleteOrphanChat = useCallback(async (orphanChatId: string) => {
    try {
      await Auth.fetchWithIdToken(`/api/chats/${orphanChatId}`, { method: "DELETE" });
    } catch (e) {
      Sentry.captureException(e);
    }
  }, []);

  const persistHelpMenuGreeting = useCallback(
    async (generationAtSchedule: number) => {
      if (generationAtSchedule !== helpMenuGreetingScheduleGenerationRef.current) return;

      const viewingProjectId = projectIdRef.current;
      const chatProjectId = activeChatProjectIdRef.current;
      const chatId = currentChatIdRef.current;

      const store = usePersistentChatStore.getState();
      if (store.streaming || store.pendingAgentResponse) return;

      const cached = queryClient.getQueryData<PersistentChatWithMessages>(chatQueryKey(chatProjectId, chatId));
      const msgs = cached?.messages ?? [];
      if (msgs.some((m) => m.metadata?.source === "help_menu_agentic")) return;

      try {
        if (chatId !== NEW_CHAT_ID) {
          const res = await Auth.fetchWithIdToken("/api/chats/add-message", {
            chatId,
            role: "assistant",
            content: HELP_MENU_GREETING_TEXT,
            metadata: HELP_MENU_GREETING_METADATA,
          });
          if (!res?.success) throw new Error("add-message failed");
          const chatWithPagination = await fetchChatPage(chatId);
          const previous = queryClient.getQueryData<PersistentChatWithMessages>(chatQueryKey(chatProjectId, chatId));
          queryClient.setQueryData(chatQueryKey(chatProjectId, chatId), mergeRefetchedChatTail(previous, chatWithPagination));
          await queryClient.invalidateQueries({ queryKey: ["chats", viewingProjectId] });
          return;
        }

        if (generationAtSchedule !== helpMenuGreetingScheduleGenerationRef.current) return;
        if (usePersistentChatStore.getState().pendingAgentResponse || usePersistentChatStore.getState().streaming) return;

        const createRes = await Auth.fetchWithIdToken("/api/chats/create", {
          projectId: viewingProjectId,
          title: null,
        });
        if (!createRes?.success || !createRes.chat?.id) throw new Error("create chat failed");

        const newChatId = createRes.chat.id as string;

        if (generationAtSchedule !== helpMenuGreetingScheduleGenerationRef.current) {
          await tryDeleteOrphanChat(newChatId);
          return;
        }
        if (usePersistentChatStore.getState().pendingAgentResponse || usePersistentChatStore.getState().streaming) {
          await tryDeleteOrphanChat(newChatId);
          return;
        }
        if (currentChatIdRef.current !== NEW_CHAT_ID) {
          await tryDeleteOrphanChat(newChatId);
          return;
        }

        const addRes = await Auth.fetchWithIdToken("/api/chats/add-message", {
          chatId: newChatId,
          role: "assistant",
          content: HELP_MENU_GREETING_TEXT,
          metadata: HELP_MENU_GREETING_METADATA,
        });
        if (!addRes?.success) throw new Error("add-message failed");

        if (generationAtSchedule !== helpMenuGreetingScheduleGenerationRef.current) {
          await tryDeleteOrphanChat(newChatId);
          return;
        }
        if (usePersistentChatStore.getState().pendingAgentResponse || usePersistentChatStore.getState().streaming) {
          await tryDeleteOrphanChat(newChatId);
          return;
        }
        if (currentChatIdRef.current !== NEW_CHAT_ID) {
          await tryDeleteOrphanChat(newChatId);
          return;
        }

        const chatWithPagination = await fetchChatPage(newChatId);
        const previousNewChat = queryClient.getQueryData<PersistentChatWithMessages>(chatQueryKey(viewingProjectId, newChatId));
        queryClient.setQueryData(chatQueryKey(viewingProjectId, newChatId), mergeRefetchedChatTail(previousNewChat, chatWithPagination));
        queryClient.setQueryData(chatQueryKey(viewingProjectId, NEW_CHAT_ID), DefaultChatModel);
        setCurrentChatId(newChatId);
        setAgenticChatState(viewingProjectId, newChatId);
        await queryClient.invalidateQueries({ queryKey: ["chats", viewingProjectId] });
      } catch (e) {
        Sentry.captureException(e);
      }
    },
    [queryClient, setCurrentChatId, tryDeleteOrphanChat],
  );

  const {
    data: chatsData,
    isLoading: isProjectChatsLoading,
    isError: isProjectChatsError,
    refetch: refetchProjectChats,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProjectChatsList(project.id, searchQuery.trim(), project.seriesId);
  const chats = chatsData?.chats ?? [];
  const effectiveChatId = currentChatId ?? NEW_CHAT_ID;
  const { data: currentChatData } = useCurrentChat(activeChatProjectId, effectiveChatId);
  const updateChatMutation = useUpdateChat();
  const deleteChatMutation = useDeleteChat();

  useEffect(() => {
    const previousProjectId = previousProjectIdRef.current;
    previousProjectIdRef.current = project.id;

    if (previousProjectId && previousProjectId !== project.id) {
      setActiveChatProjectId(project.id);
      usePersistentChatStore.getState().reset();
      dismissHelpMenuGreeting();
    }
  }, [project.id, dismissHelpMenuGreeting]);

  useEffect(() => {
    return () => {
      helpMenuGreetingScheduleGenerationRef.current += 1;
      if (helpMenuGreetingRevealTimeoutRef.current !== null) {
        clearTimeout(helpMenuGreetingRevealTimeoutRef.current);
        helpMenuGreetingRevealTimeoutRef.current = null;
      }
      usePersistentChatStore.getState().reset();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const state = getStoredChatState(project.id);
    if (!state) {
      usePersistentChatStore.getState().reset();
      queryClient.setQueryData(chatQueryKey(project.id, NEW_CHAT_ID), DefaultChatModel);
      setCurrentChatId(NEW_CHAT_ID);
      setRestoringProjectId(project.id);
      const selectionVersion = selectionVersionRef.current;
      const canRestore = () => !cancelled
        && projectIdRef.current === project.id
        && selectionVersionRef.current === selectionVersion;
      // A new browser (including a PR preview) has no local selection. The
      // project list is ordered by updated_at descending and excludes deleted chats.
      void Auth.fetchWithIdToken("/api/chats/get-by-project", { projectId: project.id, limit: 1 })
        .then((res) => {
          if (!canRestore()) return;
          if (!res?.success) throw new Error("Failed to fetch latest project chat");
          const latestChat = res.chats?.[0];
          if (!latestChat) return;
          setActiveChatProjectId(latestChat.project_id ?? project.id);
          setCurrentChatId(latestChat.id);
          setAgenticChatState(project.id, latestChat.id, latestChat.project_id);
        })
        .catch((error) => {
          if (canRestore()) Sentry.captureException(error);
        })
        .finally(() => {
          if (canRestore()) setRestoringProjectId(null);
        });
      return () => { cancelled = true; };
    }
    const restoredChatId = state.chatId;
    setActiveChatProjectId(state.chatProjectId ?? project.id);
    setCurrentChatId(restoredChatId);
    void Auth.fetchWithIdToken(
      "/api/chats/get",
      { chatId: restoredChatId, recentTurnLimit: 1 },
      null,
      { requestSource: "agentic_chat_restore" },
    )
      .then((res) => {
        if (!res?.success) throw new Error("Failed to fetch restored chat");
      })
      .catch((err: any) => {
        const status = Number(err?.status ?? err?.response?.status);
        if (status !== 404) return;
        // Recover from stale/deleted chat ids saved in localStorage.
        if (cancelled || projectIdRef.current !== project.id) return;
        // `currentChatIdRef` is synced from props every render and can briefly stay NEW_CHAT_ID after
        // setCurrentChatId(restoredChatId). Read the parent's selection via its own ref: only skip fallback
        // when the user has explicitly navigated to a different concrete chat.
        const parentChatId = currentChatIdFromPropsRef.current;
        if (parentChatId !== restoredChatId && parentChatId !== NEW_CHAT_ID) return;
        usePersistentChatStore.getState().reset();
        queryClient.setQueryData(chatQueryKey(project.id, NEW_CHAT_ID), DefaultChatModel);
        clearAgenticChatState(project.id);
        setCurrentChatId(NEW_CHAT_ID);
      });
    return () => {
      cancelled = true;
    };
  }, [project?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- validate persisted id once per project

  useEffect(() => {
    const interval = setInterval(() => {
      if (currentChatId && currentChatId !== NEW_CHAT_ID) {
        setAgenticChatState(project.id, currentChatId, activeChatProjectId);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [project.id, currentChatId, activeChatProjectId]);

  useEffect(() => {
    if (!currentChatId || currentChatId === NEW_CHAT_ID) {
      setActiveChatProjectId(project.id);
      return;
    }
    const match = chats.find((chat) => chat.id === currentChatId);
    if (match?.project_id) {
      setActiveChatProjectId(match.project_id);
    }
  }, [currentChatId, chats, project.id]);

  const refreshCharacters = useStoryBibleStore((state) => state.refreshCharacters);
  const refreshWorldbuildingEntries = useStoryBibleStore((state) => state.refreshWorldbuildingEntries);

  useEffect(() => {
    if (project.id) {
      void Promise.resolve(refreshCharacters(project.id)).catch(() => undefined);
      void Promise.resolve(refreshWorldbuildingEntries(project.id, "PersistentChat.index.mount")).catch(() => undefined);
    }
  }, [project.id, refreshCharacters, refreshWorldbuildingEntries]);

  // Listen for chat restored from trash signal and switch to that chat
  const chatRestoredSignal = useCrossAppSignalStore((state) => state.signals[CrossAppSignal.CHAT_RESTORED_FROM_TRASH]);
  const getSignalData = useCrossAppSignalStore((state) => state.getSignalData);
  const clearSignalData = useCrossAppSignalStore((state) => state.clearSignalData);
  const setSignalActive = useCrossAppSignalStore((state) => state.setSignalActive);

  useEffect(() => {
    if (chatRestoredSignal) {
      const data = getSignalData(CrossAppSignal.CHAT_RESTORED_FROM_TRASH);
      if (data?.chatId) {
        setCurrentChatId(data.chatId);
        setView("chat");
        setAgenticChatState(project.id, data.chatId);
      }
      // Reset the signal
      setSignalActive(CrossAppSignal.CHAT_RESTORED_FROM_TRASH, false);
      clearSignalData(CrossAppSignal.CHAT_RESTORED_FROM_TRASH);
    }
  }, [chatRestoredSignal, getSignalData, clearSignalData, setSignalActive, setCurrentChatId, project.id]);

  const currentChat = chats.find((c) => c.id === effectiveChatId) ?? currentChatData;
  const chatTitle = effectiveChatId === NEW_CHAT_ID ? "New chat" : sanitizeChatTitleForDisplay(currentChat?.title);

  const handleBack = useCallback(() => {
    const ctaAction = usePersistentChatStore.getState().ctaAction;
    if (ctaAction) {
      if (ctaAction.signalKey) {
        useCrossAppSignalStore.getState().setSignalActive(ctaAction.signalKey, false);
      }
      if (ctaAction.status === "in_progress") {
        usePersistentChatStore.getState().abortStream("back");
      }
      usePersistentChatStore.getState().reset("back");
    }
    queryClient.setQueryData(chatQueryKey(project.id, NEW_CHAT_ID), DefaultChatModel);
    setView("list");
    setSearchQuery("");
    setIsSearching(false);
  }, [project.id, queryClient]);

  useOnSudoLayoutEvent("trigger_cta_chat", () => {
    // A CTA may reuse NEW_CHAT_ID without changing the selection.
    selectionVersionRef.current += 1;
    setRestoringProjectId(null);
    setView("chat");
  });

  /** Piggyback on `focus_chat` (always has subscribers) so Help → Chat cannot no-op if a separate event had no listeners. */
  useOnSudoLayoutEvent("focus_chat", ({ payload }) => {
    if (payload?.source === "help_menu_agentic" || payload?.source === "chat-about-an-idea-button") {
      selectionVersionRef.current += 1;
      setRestoringProjectId(null);
    }
    if (payload?.source !== "help_menu_agentic") return;
    setView("chat");
    if (helpMenuGreetingRevealTimeoutRef.current !== null) {
      clearTimeout(helpMenuGreetingRevealTimeoutRef.current);
      helpMenuGreetingRevealTimeoutRef.current = null;
    }
    const generation = ++helpMenuGreetingScheduleGenerationRef.current;
    helpMenuGreetingRevealTimeoutRef.current = setTimeout(() => {
      helpMenuGreetingRevealTimeoutRef.current = null;
      if (generation !== helpMenuGreetingScheduleGenerationRef.current) return;
      void persistHelpMenuGreeting(generation);
    }, HELP_MENU_GREETING_AFTER_CLOSE_MS);
  }, { deps: [persistHelpMenuGreeting] });

  useEffect(() => {
    if (view === "list") {
      dismissHelpMenuGreeting();
    }
  }, [view, dismissHelpMenuGreeting]);

  const handleNewChat = useCallback(() => {
    void dismissImportConversationStarters(project);
    dismissHelpMenuGreeting();
    const ctaAction = usePersistentChatStore.getState().ctaAction;
    if (ctaAction) {
      if (ctaAction.signalKey) {
        useCrossAppSignalStore.getState().setSignalActive(ctaAction.signalKey, false);
      }
      if (ctaAction.status === "in_progress") {
        usePersistentChatStore.getState().abortStream("new_chat");
      }
    }
    usePersistentChatStore.getState().reset("new_chat");
    queryClient.setQueryData(chatQueryKey(project.id, NEW_CHAT_ID), DefaultChatModel);
    setCurrentChatId(NEW_CHAT_ID);
    setView("chat");
    setSearchQuery("");
    setIsSearching(false);
  }, [project, setCurrentChatId, queryClient, dismissHelpMenuGreeting]);

  const handleSelectChat = useCallback(
    (chatId: string, chatProjectId?: string) => {
      dismissHelpMenuGreeting();
      const owningProjectId = chatProjectId ?? project.id;
      setActiveChatProjectId(owningProjectId);
      setCurrentChatId(chatId);
      setView("chat");
      setAgenticChatState(project.id, chatId, owningProjectId);
    },
    [setCurrentChatId, project.id, dismissHelpMenuGreeting],
  );

  const handleRenameChat = useCallback(
    async (chatId: string, newTitle: string) => {
      const previousCaches = snapshotProjectChatsListCaches(queryClient, project.id);

      updateProjectChatsListCaches(queryClient, project.id, (oldChats) =>
        oldChats.map((chat) => (chat.id === chatId ? { ...chat, title: newTitle } : chat)),
      );

      try {
        await updateChatMutation.mutateAsync({ chatId, title: newTitle });
      } catch {
        restoreProjectChatsListCaches(queryClient, previousCaches);
      }
    },
    [updateChatMutation, queryClient, project.id],
  );

  const refreshTrashedItems = useProjectStore((state) => state.refreshTrashedItems);

  const handleDeleteChat = useCallback(
    async (chatId: string) => {
      const previousCaches = snapshotProjectChatsListCaches(queryClient, project.id);

      updateProjectChatsListCaches(queryClient, project.id, (oldChats) => oldChats.filter((chat) => chat.id !== chatId));

      if (currentChatId === chatId) {
        const ctaAction = usePersistentChatStore.getState().ctaAction;
        if (ctaAction) {
          if (ctaAction.signalKey) {
            useCrossAppSignalStore.getState().setSignalActive(ctaAction.signalKey, false);
          }
          if (ctaAction.status === "in_progress") {
            usePersistentChatStore.getState().abortStream("delete_chat");
          }
        }
        usePersistentChatStore.getState().reset("delete_chat");
        queryClient.setQueryData(chatQueryKey(project.id, NEW_CHAT_ID), DefaultChatModel);
        setCurrentChatId(NEW_CHAT_ID);
        setView("chat");
      }

      try {
        await deleteChatMutation.mutateAsync({ chatId });
        // Refresh the trash view to show the newly deleted chat
        refreshTrashedItems();
      } catch {
        restoreProjectChatsListCaches(queryClient, previousCaches);
        if (currentChatId === chatId) {
          setCurrentChatId(chatId);
        }
      }
    },
    [deleteChatMutation, queryClient, project.id, currentChatId, setCurrentChatId, refreshTrashedItems],
  );

  const handleLoadMoreChats = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return false;
    void fetchNextPage();
    return true;
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleOpenSettings = useCallback(() => {
    AppEventPublisher.agenticChat.chatPersonalizationOpened("navigation");
    useSudowriteStore.getState().openStyleSettings("chat-personalization", {
      advanced: false,
    });
  }, []);

  return (
    <SharedChatThemeProvider>
    <WithReset className="flex flex-col h-[calc(100%-2rem)]" data-agentic-chat-panel="true">
      <AgenticTabAttentionBridge projectId={project.id} chatId={effectiveChatId} />
      <ChatNavigation
        view={view}
        chatTitle={chatTitle}
        onBack={handleBack}
        onNewChat={handleNewChat}
        onOpenSettings={handleOpenSettings}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isSearching={isSearching}
        setIsSearching={setIsSearching}
      />

      {view === "list" ? (
        <ChatListView
          chats={chats}
          currentProjectId={project.id}
          isLoading={isProjectChatsLoading}
          isError={isProjectChatsError}
          hasMoreChats={Boolean(hasNextPage)}
          isLoadingMoreChats={isFetchingNextPage}
          onLoadMoreChats={handleLoadMoreChats}
          onRetryChats={() => {
            void refetchProjectChats();
          }}
          searchQuery={searchQuery}
          currentChatId={effectiveChatId}
          onSelectChat={handleSelectChat}
          onRenameChat={handleRenameChat}
          onDeleteChat={handleDeleteChat}
          onStartNewChat={() => {
            AppEventPublisher.agenticChat.newChat("button");
            handleNewChat();
          }}
        />
      ) : (
        <ChatErrorBoundary
          fallback={
            <WithReset className="animate-in fade-in-0 pt-2 flex-1 flex-col items-center justify-center flex text-center pt-24">
              <Text className="text-lg font-medium">Something went wrong</Text>
              <Text className="text-center text-sm">There was an error while loading your chat, please refresh the page and try again, or contact our friendly support team!</Text>
            </WithReset>
          }
        >
          <PersistentChatProvider
            editor={editor}
            setChatId={setCurrentChatId}
            chatId={effectiveChatId}
            chatProjectId={activeChatProjectId}
          >
            {restoringProjectId === project.id && <div role="status" className="p-4">Loading chat…</div>}
            {/* Keep chat event handlers mounted while hiding the unresolved composer. */}
            <div style={{ display: restoringProjectId === project.id ? "none" : "contents" }}>
              <PersistentChatUI />
            </div>
          </PersistentChatProvider>
        </ChatErrorBoundary>
      )}
    </WithReset>
    </SharedChatThemeProvider>
  );
};

export const PersistentChat: React.FC<Props> = (props) => {
  const project = useProjectStore((state) => state?.project) as Project;

  if (!project?.id) return null;

  return (
    <PersistentChatComponent
      {...props}
      project={project}
    />
  );
};
